<script setup lang="ts">
import { UiButton, UiModal, UiTextarea } from "@packages/component-library";
import { IconStar100Regular } from "@packages/icon-library";
import { computed, onMounted, Ref, ref } from "vue";
import { usePlaceReviewsStore, usePlaceStore } from "@/modules/place-module";
import { useI18n } from "vue-i18n";

const props = defineProps({
  rate: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["close", "setRef"]);
const { t } = useI18n();
const placeStore = usePlaceStore();
const placeReviewsStore = usePlaceReviewsStore();
const place = computed(() => placeStore.place?.place);
const selectRating: Ref<number> = ref(0);
const comment = ref("");
const buttonLoader = ref(false);
const showModal = ref(false);

const commentPlaceholder = computed(() =>
  selectRating.value > 3
    ? t("place.feedbackModal.commentPlaceholder1")
    : t("place.feedbackModal.commentPlaceholder2")
);

const commentHint = computed(() => {
  if (comment.value.trim().length === 0) {
    return "";
  }

  if (comment.value.trim().length > 0 && comment.value.trim().length < 15) {
    return t("place.feedbackModal.hintMessage1");
  }

  if (comment.value.trim().length >= 15 && comment.value.trim().length < 30) {
    return t("place.feedbackModal.hintMessage2");
  }

  return t("place.feedbackModal.hintMessage3");
});

const submitPlaceFeedback = () => {
  if (!buttonLoader.value) {
    buttonLoader.value = true;
    placeReviewsStore
      .leavePlaceFeedback(place.value.id, {
        rating: selectRating.value,
        message: comment.value,
      })
      .then(() => {
        placeReviewsStore.blockUserLeaveFeedback();
        placeStore.blockUserLeaveFeedback();
        emit("close");
      })
      .finally(() => {
        buttonLoader.value = false;
      });
  }
};

onMounted(() => {
  selectRating.value = props.rate;
  showModal.value = true;
});
</script>

<template>
  <UiModal
    v-model="showModal"
    size="s"
    :title="t('place.feedbackModal.title')"
    @update:modelValue="emit('close')"
  >
    <template #default>
      <div class="leave-feedback">
        <div class="leave-feedback__evaluate">
          <button
            v-for="rating in 5"
            :key="rating"
            type="button"
            :class="{ active: rating <= selectRating }"
            @click="selectRating = rating"
          >
            <IconStar100Regular />
          </button>
        </div>

        <div v-if="selectRating" class="leave-feedback__comment">
          <UiTextarea
            v-model="comment"
            :placeholder="commentPlaceholder"
            :hint-message="commentHint"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="leave-feedback__actions">
        <UiButton
          :label="t('system.cancel')"
          size="m"
          color="secondary"
          data-test-id="placeLeaveFeedback-button-cancel"
          @click="emit('close')"
        />
        <UiButton
          :label="t('system.done')"
          size="m"
          :disabled="!selectRating || buttonLoader"
          :loading="buttonLoader"
          data-test-id="placeLeaveFeedback-button-send"
          @click="submitPlaceFeedback"
        />
      </div>
    </template>
  </UiModal>
</template>

<style scoped lang="scss">
.leave-feedback {
  padding: calc($base-space * 2) 0;
  &__evaluate {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: calc($base-space * 3);

    > button {
      width: 40px;
      height: 40px;

      &:hover {
        > .icon {
          background-color: var(--color-semantic-content-normal-brand);
        }
      }

      &:has(~ button:hover) {
        > .icon {
          background-color: var(--color-semantic-content-normal-brand);
        }
      }

      &.active {
        > .icon {
          background-color: var(--color-semantic-content-normal-brand);
        }
      }

      > .icon {
        width: 100%;
        height: 100%;
        background-color: var(--color-semantic-background-active-primary);
        transition: all 0.1s ease-out;
      }
    }
  }

  &__comment {
    margin: calc($base-space * 6) 0 0;
  }

  &__actions {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: calc($base-space * 3);

    @media (max-width: 767px) {
      flex-flow: column-reverse;
      gap: calc($base-space * 2);
    }

    > button {
      @media (max-width: 767px) {
        width: 100%;
      }
    }
  }
}
</style>
